import { Node, Element, Text } from 'slate';

import { adjustPath, moveNodeOpWithConsistentTargetPath } from './utils';
import Debug from 'debug';

const debug = Debug('ot-slate:validate');

const validateComponent = (editor, op) => {
  if (!editor) {
    console.warn("Can't validate without an editor value, skipping op");
    return;
  }

  if (debug.enabled) {
    debug(`Validating ${JSON.stringify(op)}`);
  }

  switch (op.type) {
    case 'insert_text': {
      const targetNode = Node.get(editor, op.path);
      if (!Text.isText(targetNode)) {
        throw new Error(`insert_text can't insert text into a non-text node`);
      }
      const textLength = targetNode.text.length;
      if (op.offset > textLength) {
        throw new Error(
          `insert_text is trying to insert text at offset ${op.offset}, but the node is only ${textLength} characters long`
        );
      }
      break;
    }
    case 'remove_text': {
      const targetNode = Node.get(editor, op.path);
      if (!Text.isText(targetNode)) {
        throw new Error(`remove_text can't remove text from a non-text node`);
      }
      const charactersToDelete = targetNode.text.slice(
        op.offset,
        op.offset + op.text.length
      );
      if (charactersToDelete !== op.text) {
        throw new Error(
          `remove_text is trying to remove "${op.text}", but the text at ${op.offset} is "${charactersToDelete}"`
        );
      }
      break;
    }
    case 'split_node': {
      const targetNode = Node.get(editor, op.path);
      const childCount = Element.isElement(targetNode)
        ? targetNode.children.length
        : targetNode.text.length;
      if (op.position > childCount) {
        throw new Error(
          `split_node has position ${op.position}, but the node at path ${op.path} only has ${childCount} children`
        );
      }
      break;
    }
    case 'merge_node': {
      const sourceNode = Node.get(editor, op.path);
      if (op.path[op.path.length - 1] === 0) {
        throw new Error(
          `merge_node cannot operate on a first child node: ${op.path}`
        );
      }
      const targetPath = adjustPath(op.path, -1, op.path.length - 1);
      const targetNode = Node.get(editor, targetPath);

      if (Element.isElement(targetNode) !== Element.isElement(sourceNode)) {
        throw new Error(
          `merge_node cannot merge nodes of two different types. Source: ${
            Element.isElement(sourceNode) ? 'Element' : 'Leaf'
          }
          }, target: ${Element.isElement(targetNode) ? 'Element' : 'Leaf'}`
        );
      }
      const childCount = Element.isElement(targetNode)
        ? targetNode.children.length
        : targetNode.text.length;
      if (childCount !== op.position) {
        throw new Error(
          `merge_node has position ${op.position}, but the node at path ${targetPath} has ${childCount} children`
        );
      }
      break;
    }
    case 'move_node': {
      Node.get(editor, op.path);
      const consistentOp = moveNodeOpWithConsistentTargetPath(op);

      const parentPathOfTarget = op.newPath.slice(0, -1);
      Node.get(editor, parentPathOfTarget);

      const parentOfTarget = Node.get(editor, parentPathOfTarget);
      const lastPathComponent =
        consistentOp.newPath[consistentOp.newPath.length - 1];
      const childCount = parentOfTarget.children.length;
      if (lastPathComponent > childCount) {
        throw new Error(
          `move_node to position ${op.newPath} is not valid. ${parentPathOfTarget} only has ${childCount} children.`
        );
      }
      break;
    }
    case 'insert_node': {
      // Very similar to the 2nd half of the move_node check.
      const parentPathOfTarget = op.path.slice(0, -1);
      Node.get(editor, parentPathOfTarget);

      const parentOfTarget = Node.get(editor, parentPathOfTarget);
      const lastPathComponent = op.path[op.path.length - 1];
      const childCount = parentOfTarget.children.length;
      if (lastPathComponent > childCount) {
        throw new Error(
          `insert_node at position ${op.path} is not valid. ${parentPathOfTarget} only has ${childCount} children.`
        );
      }
      break;
    }
    case 'remove_node': {
      Node.get(editor, op.path);
      break;
    }
    case 'set_selection': {
      if (!op.newProperties) break;

      const {
        newProperties: { anchor, focus },
      } = op;
      const anchorNode = Node.get(editor, anchor.path);
      const focusNode = Node.get(editor, focus.path);

      if (anchor.offset < 0) {
        throw new Error(
          `set_selection is trying to move anchorOffset to invalid offset ${anchor.offset}`
        );
      }

      if (focus.offset < 0) {
        throw new Error(
          `set_selection is trying to move focusOffset to invalid offset ${focus.offset}`
        );
      }

      const anchorLength = anchorNode.text.length;
      if (anchor.offset > anchorLength) {
        throw new Error(
          `set_selection is trying to move anchorOffset to ${anchor.offset}, but ${anchor.path} only has ${anchorLength} characters`
        );
      }

      const focusLength = focusNode.text.length;
      if (focus.offset > focusLength) {
        throw new Error(
          `set_selection is trying to move focusOffset to ${focus.offset}, but ${focus.path} only has ${focusLength} characters`
        );
      }
      break;
    }
  }
};

export { validateComponent };
